import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Form,
} from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  CornerDownLeft,
  FileText,
  StickyNote,
  Shield,
  Paperclip,
  Trash2,
  Clock,
  UserCircle2,
  Eye,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import Swal from "sweetalert2";

import { getCaseById } from "../../services/caseService";
import {
  getCaseNotesByCaseId,
  createCaseNote,
  deleteCaseNote,
  deleteCaseNoteAttachment,
} from "../../services/caseNoteService";

import {
  uploadFiles,
  openFileInNewTab,
  saveFile,
} from "../../services/fileService";
import { formatDateTime } from "../../utils/formatters";
import usePermission from "../../hooks/usePermission";

// Select de catálogos genéricos
import SelectCategory from "../../components/Common/SelectCategory";

import "./CaseManagePage.css";

// Código de categoría para tipos de documento de notas del caso
const CASE_NOTE_DOC_TYPE_CATEGORY_CODE = "case_note_doc_type";

// ----------------------------------------------------
// Helpers de agrupación por fecha (timeline)
// ----------------------------------------------------
const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const toDateKey = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatDateGroupLabel = (date) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(date, today)) return "Hoy";
  if (isSameDay(date, yesterday)) return "Ayer";

  return date.toLocaleDateString("es-CO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const groupNotesByDate = (notes) => {
  const map = new Map();

  notes.forEach((note) => {
    const d = new Date(note.createdAt);
    if (Number.isNaN(d.getTime())) return;
    const key = toDateKey(note.createdAt);

    if (!map.has(key)) {
      map.set(key, { date: d, items: [] });
    }
    map.get(key).items.push(note);
  });

  return Array.from(map.entries())
    .sort((a, b) => b[1].date - a[1].date) // más recientes arriba
    .map(([key, value]) => ({
      key,
      date: value.date,
      label: formatDateGroupLabel(value.date),
      items: value.items.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      ),
    }));
};

const CaseManagePage = () => {
  const { id: caseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Ruta por defecto en caso de no venir de lista
  const fallbackPath = "/dashboard/cases";

  // Si venimos de my-cases o de cases, viene aquí
  const from = location.state?.from || fallbackPath;

  const hasPermission = usePermission();

  const canUpdate = hasPermission("casos.gestion");

  const [caseDetail, setCaseDetail] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loadingCase, setLoadingCase] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [error, setError] = useState(null);

  // Archivos seleccionados en el formulario (aún sin subir)
  // Estructura: { file: File, notes: string }
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Grupos de timeline (acordeón)
  const [expandedGroups, setExpandedGroups] = useState({});

  // ----------------------------------------------------
  // Cargar datos del caso y notas
  // ----------------------------------------------------
  const loadCase = useCallback(async () => {
    try {
      setLoadingCase(true);
      const res = await getCaseById(caseId);
      if (!res?.success)
        throw new Error(res?.message || "Error al cargar caso");
      setCaseDetail(res.data);
    } catch (err) {
      console.error(err);
      setError("No se pudo cargar la información del caso.");
    } finally {
      setLoadingCase(false);
    }
  }, [caseId]);

  const loadNotes = useCallback(async () => {
    try {
      setLoadingNotes(true);
      const res = await getCaseNotesByCaseId(caseId, true);
      if (!res?.success)
        throw new Error(res?.message || "Error al cargar notas");
      const sorted = [...(res.data || [])].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setNotes(sorted);
    } catch (err) {
      console.error(err);
      setError("No se pudo cargar el historial de gestiones.");
    } finally {
      setLoadingNotes(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (!caseId) return;
    loadCase();
    loadNotes();
  }, [caseId, loadCase, loadNotes]);

  // ----------------------------------------------------
  // Esquema de validación (dinámico según haya archivos o no)
  // ----------------------------------------------------
  const validationSchema = useMemo(() => {
    const hasFiles = attachmentFiles.length > 0;

    return Yup.object().shape({
      title: Yup.string()
        .trim()
        .required("El título de la gestión es obligatorio.")
        .max(200, "El título no debe superar los 200 caracteres."),
      body: Yup.string()
        .trim()
        .required("Describe brevemente qué se hizo en la gestión.")
        .min(10, "La descripción debe tener al menos 10 caracteres.")
        .max(4000, "La descripción no debe superar los 4000 caracteres."),
      isInternal: Yup.boolean(),
      attachmentTypeId: hasFiles
        ? Yup.string()
            .required(
              "Debes seleccionar el tipo de documento para los adjuntos."
            )
            .uuid("El tipo de documento seleccionado no es válido.")
        : Yup.string().nullable(),
    });
  }, [attachmentFiles.length]);

  // ----------------------------------------------------
  // Formik: gestión de la nueva nota
  // ----------------------------------------------------
  const formik = useFormik({
    initialValues: {
      title: "",
      body: "",
      isInternal: false,
      attachmentTypeId: "", // CategoryItemId de los adjuntos
    },
    validationSchema,
    enableReinitialize: false,
    onSubmit: async (values, { resetForm }) => {
      if (!canUpdate) {
        Swal.fire(
          "Permisos",
          "No tienes permisos para gestionar el caso.",
          "warning"
        );
        return;
      }

      try {
        setSubmitting(true);

        // 1) Subir archivos (si hay)
        let attachments = [];
        if (attachmentFiles.length > 0) {
          // Construimos un módulo específico por caso
          const moduleKey = `legal/cases/${caseId}`;
          // Subimos solo los File reales
          const uploaded = await uploadFiles({
            files: attachmentFiles.map((x) => x.file),
            module: moduleKey,
            referenceType: "case-note",
          });

          // Mapeamos cada StoredFileDto al payload de adjuntos, alineando por índice
          attachments = (uploaded || []).map((fileDto, index) => {
            const meta = attachmentFiles[index];
            const rawNotes = meta?.notes ?? "";

            return {
              attachmentTypeId: values.attachmentTypeId, // tomado del SelectCategory
              storedFileId: fileDto.id,
              notes: rawNotes.trim().length > 0 ? rawNotes.trim() : null,
            };
          });
        }

        // 2) Construir payload CaseNoteCreateDto
        const payload = {
          caseId,
          title: values.title.trim(),
          body: values.body.trim(),
          isInternal: values.isInternal,
          attachments,
        };

        const res = await createCaseNote(payload);
        if (!res?.success) {
          throw new Error(res?.message || "Error al registrar la nota.");
        }

        Swal.fire(
          "Registrado",
          "La gestión fue registrada correctamente.",
          "success"
        );
        resetForm();
        setAttachmentFiles([]);
        await loadNotes();
      } catch (err) {
        console.error(err);
        Swal.fire(
          "Error",
          err?.message || "No se pudo registrar la gestión del caso.",
          "error"
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ----------------------------------------------------
  // Manejo de archivos en el formulario
  // ----------------------------------------------------
  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Convertimos cada File en un objeto { file, notes }
    const mapped = files.map((f) => ({
      file: f,
      notes: "",
    }));

    setAttachmentFiles((prev) => [...prev, ...mapped]);
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Actualiza la nota asociada a un archivo específico
  const handleChangeFileNote = (index, value) => {
    setAttachmentFiles((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              notes: value,
            }
          : item
      )
    );
  };

  // ----------------------------------------------------
  // Acciones sobre notas existentes
  // ----------------------------------------------------
  const handleDeleteNote = async (noteId) => {
    const result = await Swal.fire({
      title: "¿Eliminar gestión?",
      text: "Esta acción eliminará la nota registrada en este caso.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deleteCaseNote(noteId);
      if (!res?.success)
        throw new Error(res?.message || "No se pudo eliminar la nota.");
      Swal.fire(
        "Eliminada",
        "La gestión fue eliminada correctamente.",
        "success"
      );
      await loadNotes();
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err?.message || "Ocurrió un error al eliminar la gestión.",
        "error"
      );
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    const result = await Swal.fire({
      title: "¿Eliminar adjunto?",
      text: "El archivo será eliminado de esta gestión.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deleteCaseNoteAttachment(attachmentId);
      if (!res?.success)
        throw new Error(res?.message || "No se pudo eliminar el adjunto.");
      Swal.fire(
        "Eliminado",
        "El adjunto fue eliminado correctamente.",
        "success"
      );
      await loadNotes();
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err?.message || "Ocurrió un error al eliminar el adjunto.",
        "error"
      );
    }
  };

  // ----------------------------------------------------
  // Helpers de UI
  // ----------------------------------------------------
  const caseHeader = useMemo(() => {
    if (!caseDetail) return null;
    return (
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <FileText size={20} className="text-primary" />
                <h5 className="mb-0 text-dark">
                  {caseDetail.internalCode ||
                    caseDetail.caseNumber ||
                    "Caso sin código"}
                </h5>
              </div>
              <p className="mb-1 text-muted">{caseDetail.title}</p>
              <div className="small text-muted">
                Cliente: <strong>{caseDetail.customerName}</strong> · Abogado:{" "}
                <strong>{caseDetail.lawyerResponsibleName}</strong>
              </div>
            </div>
            <div className="d-flex flex-column align-items-end gap-2">
              <div>
                <Badge bg="info" className="me-1">
                  {caseDetail.caseStatusName || "Sin estado"}
                </Badge>
                {caseDetail.priorityName && (
                  <Badge bg="warning" text="dark">
                    {caseDetail.priorityName}
                  </Badge>
                )}
              </div>
              {caseDetail.filingDate && (
                <small className="text-muted text-dark">
                  Creado el:{" "}
                  <strong>{formatDateTime(caseDetail.createdAt)}</strong>
                </small>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }, [caseDetail]);

  const groupedNotes = useMemo(() => groupNotesByDate(notes), [notes]);

  // Inicializar expansión por defecto (todas abiertas) cuando cambian los grupos
  useEffect(() => {
    if (!groupedNotes || groupedNotes.length === 0) return;
    setExpandedGroups((prev) => {
      const next = { ...prev };
      groupedNotes.forEach((g) => {
        if (next[g.key] === undefined) {
          next[g.key] = true; // por defecto expandido
        }
      });
      return next;
    });
  }, [groupedNotes]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (error && !loadingCase && !loadingNotes) {
    return (
      <Container className="py-4">
        <Alert variant="danger" className="d-flex justify-content-between">
          <span>{error}</span>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => {
              setError(null);
              loadCase();
              loadNotes();
            }}
          >
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      {/* Header superior */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1">Gestión del caso</h3>
          <p className="text-muted small mb-0">
            Registra las gestiones, notas y adjuntos importantes asociados a
            este caso.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate(from)}
          className="d-flex align-items-center gap-1"
        >
          <CornerDownLeft size={16} />
          Volver a casos
        </Button>
      </div>

      {loadingCase ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        caseHeader
      )}

      <Row className="mt-3">
        {/* Columna izquierda: formulario de nueva gestión */}
        <Col lg={5} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom-0 pb-0">
              <div className="d-flex align-items-center gap-2">
                <StickyNote size={18} className="text-primary" />
                <div>
                  <h6 className="mb-0 text-dark">Registrar nueva gestión</h6>
                  <small className="text-dark">
                    Describe qué se hizo y adjunta soportes si es necesario.
                  </small>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={formik.handleSubmit} noValidate>
                {/* Título */}
                <Form.Group className="mb-3 text-dark" controlId="noteTitle">
                  <Form.Label>Título de la gestión</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="Ej: Gestión de radicado 1234"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.title && !!formik.errors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.title}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Descripción */}
                <Form.Group className="mb-3 text-dark" controlId="noteBody">
                  <Form.Label>Descripción</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="body"
                    placeholder="Describe brevemente la gestión realizada, por ejemplo: se presentó memorial de subsanación al Juzgado 15 Civil..."
                    value={formik.values.body}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.body && !!formik.errors.body}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.body}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Nota interna / cliente */}
                <Form.Group
                  className="mb-3 text-dark"
                  controlId="noteIsInternal"
                >
                  <Form.Check
                    type="switch"
                    name="isInternal"
                    label={
                      <span>
                        <Shield size={14} className="me-1 text-warning" />
                        Nota interna (solo visible para el equipo interno)
                      </span>
                    }
                    checked={formik.values.isInternal}
                    onChange={formik.handleChange}
                  />
                  <Form.Text className="text-dark small">
                    Desactiva esta opción si la nota puede ser visible para el
                    cliente en futuros portales o reportes.
                  </Form.Text>
                </Form.Group>

                {/* Adjuntos */}
                <Form.Group className="mb-3 text-dark">
                  <Form.Label>Adjuntar archivos</Form.Label>
                  <div className="border rounded-3 p-3 bg-light">
                    {/* Tipo de documento de los adjuntos */}
                    <Row className="mb-2">
                      <Col>
                        <Form.Group controlId="attachmentTypeId">
                          <Form.Label className="small mb-1">
                            Tipo de documento de los adjuntos
                          </Form.Label>
                          <SelectCategory
                            categoryCode={CASE_NOTE_DOC_TYPE_CATEGORY_CODE}
                            showCode={false}
                            value={formik.values.attachmentTypeId}
                            onChange={(val) =>
                              formik.setFieldValue("attachmentTypeId", val)
                            }
                            placeholder="Seleccione tipo de documento"
                          />
                          {formik.touched.attachmentTypeId &&
                            formik.errors.attachmentTypeId && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.attachmentTypeId}
                              </div>
                            )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <Paperclip size={16} className="text-muted" />
                        <span className="small text-dark">
                          Puedes adjuntar memoriales, autos, notificaciones,
                          etc.
                        </span>
                      </div>
                      <div>
                        <Form.Label
                          className="btn btn-sm btn-outline-primary mb-0"
                          style={{ cursor: "pointer" }}
                        >
                          Seleccionar archivos
                          <Form.Control
                            type="file"
                            multiple
                            className="d-none"
                            onChange={handleFilesChange}
                          />
                        </Form.Label>
                      </div>
                    </div>

                    {attachmentFiles.length > 0 && (
                      <div className="mt-2">
                        <ul className="list-unstyled mb-0 small">
                          {attachmentFiles.map((item, idx) => (
                            <li
                              key={`${item.file.name}-${idx}`}
                              className="py-2 border-bottom"
                            >
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <div className="d-flex align-items-center gap-2">
                                  <Paperclip size={14} className="text-muted" />
                                  <span>{item.file.name}</span>
                                  <span className="text-muted">
                                    ({(item.file.size / 1024).toFixed(1)} KB)
                                  </span>
                                </div>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-danger p-0"
                                  onClick={() => handleRemoveFile(idx)}
                                  title="Quitar archivo"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>

                              {/* Nota opcional del adjunto */}
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Nota del adjunto (opcional). Ej: 'Radicado principal presentado el 15/11/2025.'"
                                value={item.notes}
                                onChange={(e) =>
                                  handleChangeFileNote(idx, e.target.value)
                                }
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {attachmentFiles.length === 0 && (
                      <p className="small text-dark mb-0 mt-2">
                        Aún no has seleccionado archivos.
                      </p>
                    )}
                  </div>
                </Form.Group>

                <div className="d-flex justify-content-end mt-3">
                  <Button
                    type="submit"
                    disabled={submitting || loadingCase}
                    className="d-flex align-items-center gap-2"
                  >
                    {submitting && (
                      <Spinner
                        size="sm"
                        animation="border"
                        role="status"
                        className="me-1"
                      />
                    )}
                    Registrar gestión
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Columna derecha: historial de gestiones (TIMELINE) */}
        <Col lg={7} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom-0 pb-0">
              <div className="d-flex align-items-center gap-2">
                <StickyNote size={18} className="text-secondary" />
                <div>
                  <h6 className="mb-0 text-dark">Historial de gestiones</h6>
                  <small className="text-dark">
                    Timeline de notas agrupadas por fecha, de la más reciente a
                    la más antigua.
                  </small>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {loadingNotes ? (
                <div className="text-center my-4">
                  <Spinner animation="border" />
                </div>
              ) : groupedNotes.length === 0 ? (
                <div className="text-center py-4">
                  <StickyNote size={32} className="text-muted mb-2" />
                  <p className="mb-1 text-muted">
                    Aún no hay gestiones registradas.
                  </p>
                  <small className="text-muted">
                    Usa el panel de la izquierda para registrar la primera nota
                    del caso.
                  </small>
                </div>
              ) : (
                <div className="cm-timeline">
                  {groupedNotes.map((group) => {
                    const isExpanded = expandedGroups[group.key] ?? true;

                    return (
                      <div key={group.key} className="cm-timeline-group">
                        {/* Header de fecha (acordeón) */}
                        <div
                          className="cm-timeline-group-header"
                          onClick={() => toggleGroup(group.key)}
                        >
                          <span className="cm-timeline-date-pill">
                            <Clock size={14} className="text-muted" />
                            {group.label}
                          </span>
                          <span className="cm-timeline-toggle-icon">
                            {isExpanded ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </span>
                        </div>

                        {/* Línea + items (colapsable) */}
                        {isExpanded && (
                          <div className="cm-timeline-line">
                            {group.items.map((note) => (
                              <div key={note.id} className="cm-timeline-item">
                                <span className="cm-timeline-dot" />

                                <div className="ms-2">
                                  <Card className="cm-note-card text-dark">
                                    <Card.Body>
                                      <div className="d-flex justify-content-between align-items-start mb-1">
                                        <div>
                                          <div className="cm-note-header-title">
                                            <h6 className="mb-0">
                                              {note.title}
                                            </h6>
                                            {note.isInternal ? (
                                              <span className="cm-badge-internal">
                                                <Shield
                                                  size={11}
                                                  className="me-1"
                                                />
                                                Interna
                                              </span>
                                            ) : (
                                              <span className="cm-badge-external">
                                                Visible para cliente
                                              </span>
                                            )}
                                          </div>
                                          <div className="cm-note-meta d-flex align-items-center gap-2 flex-wrap mt-1 text-dark">
                                            <UserCircle2 size={14} />
                                            <span>
                                              {note.authorDisplayName ||
                                                "Usuario desconocido"}
                                            </span>
                                            <span>·</span>
                                            <Clock size={12} />
                                            <span>
                                              {formatDateTime(note.createdAt)}
                                            </span>
                                          </div>
                                        </div>

                                        {canUpdate && (
                                          <Button
                                            variant="link"
                                            size="sm"
                                            className="cm-note-delete p-0"
                                            onClick={() =>
                                              handleDeleteNote(note.id)
                                            }
                                            title="Eliminar esta gestión"
                                          >
                                            <Trash2 size={16} />
                                          </Button>
                                        )}
                                      </div>

                                      {/* Texto */}
                                      <p className="cm-note-body text-dark">
                                        {note.body}
                                      </p>

                                      {/* Adjuntos */}
                                      {note.attachments &&
                                        note.attachments.length > 0 && (
                                          <div className="mt-2">
                                            <div className="cm-att-header d-flex align-items-center gap-1 mb-1 text-dark">
                                              <Paperclip size={14} />
                                              Archivos adjuntos (
                                              {note.attachments.length})
                                            </div>
                                            <ul className="list-unstyled mb-0">
                                              {note.attachments.map((att) => (
                                                <li
                                                  key={att.id}
                                                  className="d-flex justify-content-between align-items-center cm-att-row"
                                                >
                                                  <div className="d-flex flex-column">
                                                    <div className="d-flex align-items-center gap-2">
                                                      <Paperclip
                                                        size={13}
                                                        className="text-muted"
                                                      />
                                                      <span className="fw-semibold">
                                                        {att.fileName ||
                                                          "Archivo"}
                                                      </span>
                                                      {att.attachmentTypeName && (
                                                        <Badge
                                                          bg="light"
                                                          text="dark"
                                                        >
                                                          {
                                                            att.attachmentTypeName
                                                          }
                                                        </Badge>
                                                      )}
                                                    </div>
                                                    <div className="text-muted">
                                                      {att.fileSize
                                                        ? `${(
                                                            att.fileSize / 1024
                                                          ).toFixed(1)} KB`
                                                        : ""}
                                                      {att.notes && (
                                                        <>
                                                          {" · "}
                                                          <span>
                                                            {att.notes}
                                                          </span>
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>

                                                  <div className="d-flex align-items-center gap-1 cm-att-actions">
                                                    <Button
                                                      variant="outline-secondary"
                                                      size="sm"
                                                      onClick={() =>
                                                        openFileInNewTab(
                                                          att.storedFileId
                                                        )
                                                      }
                                                      title="Ver / abrir"
                                                    >
                                                      <Eye size={14} />
                                                    </Button>
                                                    <Button
                                                      variant="outline-secondary"
                                                      size="sm"
                                                      onClick={() =>
                                                        saveFile(
                                                          att.storedFileId
                                                        )
                                                      }
                                                      title="Descargar"
                                                    >
                                                      <i className="bi bi-download" />
                                                    </Button>
                                                    {canUpdate && (
                                                      <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() =>
                                                          handleDeleteAttachment(
                                                            att.id
                                                          )
                                                        }
                                                        title="Eliminar adjunto"
                                                      >
                                                        <Trash2 size={14} />
                                                      </Button>
                                                    )}
                                                  </div>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                    </Card.Body>
                                  </Card>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CaseManagePage;
